import { Button, Plus, Skeleton } from '../../../../modules/design-system';
import type { ShippingAddress } from '../../../../modules/api';
import { AddressBlock } from '../../shared';

export interface YourAddressSectionProps {
  address: ShippingAddress | null;
  /** True while saved addresses are loading — show a skeleton instead of the
   *  empty state so a returning user's default doesn't flash "Add address". */
  loading?: boolean;
  /** Empty-state CTA — open the create-address form. */
  onClickAdd: () => void;
  /** "Change" on the chosen address — open the saved-address picker. */
  onChange?: () => void;
}

/**
 * "Your Address" section of the New Delivery form. Render modes:
 *   • loading          → skeleton card.
 *   • address === null → empty state + "Add My Address" CTA.
 *   • address set      → AddressBlock summary with a "Change" link that opens
 *                        the saved-address picker.
 *
 * Lives inside the outer Delivery glass container — no glass of its own.
 */
export function YourAddressSection({
  address,
  loading = false,
  onClickAdd,
  onChange,
}: YourAddressSectionProps) {
  return (
    <section data-co-component="DeliveryYourAddress">
      <h3
        className="font-bold tracking-[-0.3px]"
        style={{
          color: 'var(--co-DeliveryYourAddress-titleColor, #FFFFFF)',
          fontSize: 'var(--co-DeliveryYourAddress-titleSize, 17px)',
        }}
      >
        Your Address
      </h3>

      {loading ? (
        <div className="mt-3">
          <Skeleton className="h-[92px] w-full rounded-[20px]" />
        </div>
      ) : address === null ? (
        <div
          className="mt-3 rounded-[20px] p-4"
          style={{
            border:
              '1px solid var(--co-DeliveryYourAddress-emptyStateBorder, rgba(171,174,179,0.20))',
            background:
              'linear-gradient(to bottom, ' +
              'var(--co-DeliveryYourAddress-emptyStateBgFrom, rgba(255,255,255,0.02)) 0%, ' +
              'var(--co-DeliveryYourAddress-emptyStateBgTo, rgba(255,255,255,0.02)) 100%)',
          }}
        >
          <p className="text-[14px] leading-[20px]">
            <span
              className="font-semibold"
              style={{
                color: 'var(--co-DeliveryYourAddress-emptyStatePromptText, #FFFFFF)',
              }}
            >
              Add shipping address
            </span>
            <span
              style={{
                color: 'var(--co-DeliveryYourAddress-emptyStateSubText, rgba(255,255,255,0.70))',
              }}
            >
              {' '}for where you would like to receive your package
            </span>
          </p>
          <div className="mt-4">
            <Button
              variant="primary"
              size="md"
              onClick={onClickAdd}
              rightIcon={<Plus size={18} strokeWidth={2.4} />}
            >
              Add My Address
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-3">
          <AddressBlock address={address} {...(onChange ? { onChange } : {})} />
        </div>
      )}
    </section>
  );
}
